import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { sambaApi } from '@/lib/pillarApi';
import { getSambaNativeRuntime } from '@/lib/sambaNativeRuntime';
import {
    hasDownloadsCapability,
    InstallerDownloadLinkTarget,
    SambaLatestReleaseResponseSchema,
    type ActiveRoleAssignmentResponse,
    type SambaArch,
    type SambaLatestReleaseQuery,
    type SambaPackageType,
    type SambaLatestReleaseResponse,
    type SambaWindowsPackageRelease,
    type WindowsArtifactQa,
} from '@pillar-portal/api-types';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

type SupportedOs = 'win' | 'mac';

type ClientDownloadDefaults = {
    os: SupportedOs;
    windowsArch: SambaArch;
};

type DownloadNavigatorUserAgentData = {
    platform?: string;
    architecture?: string;
};

type DownloadNavigator = {
    platform: string;
    userAgent: string;
    userAgentData?: DownloadNavigatorUserAgentData;
};

type DownloadRuntimeGlobal = {
    navigator?: DownloadNavigator;
};

const osSelectionSchema: z.ZodType<SupportedOs> = z.union([z.literal('win'), z.literal('mac')]);
const windowsArchSelectionSchema: z.ZodType<SambaArch> = z.union([
    z.literal('x64'),
    z.literal('arm64'),
]);
const windowsPackageTypeSelectionSchema: z.ZodType<SambaPackageType> = z.union([
    z.literal('portable'),
    z.literal('installer'),
]);
export const navigatorUserAgentDataSchema: z.ZodType<DownloadNavigatorUserAgentData> = z.object({
    platform: z.string().optional(),
    architecture: z.string().optional(),
});
export const downloadNavigatorSchema: z.ZodType<DownloadNavigator> = z.object({
    platform: z.string(),
    userAgent: z.string(),
    userAgentData: navigatorUserAgentDataSchema.optional(),
});
export const downloadRuntimeGlobalSchema: z.ZodType<DownloadRuntimeGlobal> = z.object({
    navigator: downloadNavigatorSchema.optional(),
});

const buildReleaseCacheKey = (query: SambaLatestReleaseQuery): string => {
    return `${query.platform}:${query.arch}`;
};

type InstallerQaRow = {
    property: string;
    value: string;
};

const installerQaBooleanSchema = z.boolean();
const installerQaStringSchema = z.string();
const installerQaNumberSchema = z.number();
const installerQaArraySchema = z.array(z.unknown());
const installerQaObjectSchema = z.record(z.string(), z.unknown());

const flattenInstallerQa = (
    value: unknown,
    propertyPath: string = '',
    rows: InstallerQaRow[] = []
): InstallerQaRow[] => {
    if (value === null) {
        rows.push({ property: propertyPath, value: '—' });
        return rows;
    }
    const booleanResult = installerQaBooleanSchema.safeParse(value);
    if (booleanResult.success) {
        rows.push({ property: propertyPath, value: booleanResult.data ? 'Pass' : 'Fail' });
        return rows;
    }
    const stringResult = installerQaStringSchema.safeParse(value);
    if (stringResult.success) {
        rows.push({ property: propertyPath, value: stringResult.data });
        return rows;
    }
    const numberResult = installerQaNumberSchema.safeParse(value);
    if (numberResult.success) {
        rows.push({ property: propertyPath, value: String(numberResult.data) });
        return rows;
    }
    const arrayResult = installerQaArraySchema.safeParse(value);
    if (arrayResult.success) {
        arrayResult.data.forEach((entry, index) =>
            flattenInstallerQa(entry, `${propertyPath}[${index}]`, rows)
        );
        return rows;
    }
    const objectResult = installerQaObjectSchema.safeParse(value);
    if (objectResult.success) {
        for (const key of Object.keys(objectResult.data).sort()) {
            const childPath = propertyPath === '' ? key : `${propertyPath}.${key}`;
            flattenInstallerQa(objectResult.data[key], childPath, rows);
        }
        return rows;
    }
    throw new Error(`Unsupported installer QA value at ${propertyPath}.`);
};

const getReleaseQuery = (os: SupportedOs, windowsArch: SambaArch): SambaLatestReleaseQuery => {
    if (os === 'mac') {
        return { platform: 'mac', arch: windowsArch };
    }
    return { platform: 'win', arch: windowsArch };
};

const resolveSupportedOs = (input: string): SupportedOs | null => {
    const normalizedInput = input.toLowerCase();
    if (
        normalizedInput.includes('windows') ||
        normalizedInput.includes('win32') ||
        normalizedInput.includes('win64') ||
        normalizedInput.includes('wow64')
    ) {
        return 'win';
    }
    if (normalizedInput.includes('mac') || normalizedInput.includes('darwin')) {
        return 'mac';
    }
    return null;
};

const resolveArch = (input: string, fallback: SambaArch): SambaArch => {
    const normalizedInput = input.toLowerCase();
    if (normalizedInput.includes('arm64') || normalizedInput.includes('aarch64')) {
        return 'arm64';
    }
    if (
        normalizedInput.includes('x86') ||
        normalizedInput.includes('x64') ||
        normalizedInput.includes('amd64')
    ) {
        return 'x64';
    }
    return fallback;
};

export const detectClientDownloadDefaults = (): ClientDownloadDefaults => {
    const nativeRuntime = getSambaNativeRuntime();
    if (nativeRuntime?.platform === 'mac') {
        return {
            os: 'mac',
            windowsArch: nativeRuntime.arch === 'arm64' ? 'arm64' : 'x64',
        };
    }
    if (nativeRuntime?.platform === 'win') {
        return {
            os: 'win',
            windowsArch: nativeRuntime.arch === 'arm64' ? 'arm64' : 'x64',
        };
    }

    const parsedGlobal = downloadRuntimeGlobalSchema.safeParse(globalThis);
    if (!parsedGlobal.success || parsedGlobal.data.navigator === undefined) {
        return { os: 'win', windowsArch: 'x64' };
    }

    const userAgentData = parsedGlobal.data.navigator.userAgentData;
    if (userAgentData !== undefined) {
        const userAgentDataText = [
            userAgentData.platform ?? '',
            userAgentData.architecture ?? '',
        ].join(' ');
        const userAgentDataOs = resolveSupportedOs(userAgentDataText);
        if (userAgentDataOs !== null) {
            return {
                os: userAgentDataOs,
                windowsArch: resolveArch(
                    userAgentDataText,
                    userAgentDataOs === 'mac' ? 'arm64' : 'x64'
                ),
            };
        }
    }

    const platform = parsedGlobal.data.navigator.platform;
    const userAgent = parsedGlobal.data.navigator.userAgent;
    const combinedPlatform = `${platform} ${userAgent}`;

    const os: SupportedOs = resolveSupportedOs(combinedPlatform) ?? 'win';
    const windowsArch: SambaArch = resolveArch(combinedPlatform, os === 'mac' ? 'arm64' : 'x64');

    return {
        os,
        windowsArch,
    };
};

const selectWindowsPackage = (
    response: SambaLatestReleaseResponse | null | undefined,
    arch: SambaArch,
    packageType: SambaPackageType
): SambaWindowsPackageRelease | null => {
    const windowsTrack = response?.windowsTracks?.find(
        (candidateTrack) => candidateTrack.track === 'stable'
    );
    if (!windowsTrack) {
        return null;
    }
    return (
        windowsTrack.packages.find(
            (candidatePackage) =>
                candidatePackage.arch === arch && candidatePackage.packageType === packageType
        ) ?? null
    );
};

function DownloadsContent() {
    const [clientDefaults, setClientDefaults] = useState<ClientDownloadDefaults>(() =>
        detectClientDownloadDefaults()
    );
    const [selectedOs, setSelectedOs] = useState<SupportedOs>(
        () => detectClientDownloadDefaults().os
    );
    const [selectedWindowsArch, setSelectedWindowsArch] = useState<SambaArch>(
        () => detectClientDownloadDefaults().windowsArch
    );
    const [selectedWindowsPackageType, setSelectedWindowsPackageType] =
        useState<SambaPackageType>('installer');
    const [releaseCache, setReleaseCache] = useState<Record<string, SambaLatestReleaseResponse>>(
        {}
    );
    const releaseRequestCacheRef = useRef<
        Record<string, Promise<SambaLatestReleaseResponse> | undefined>
    >({});
    const installerQaTriggerRef = useRef<HTMLButtonElement | null>(null);
    const installerQaRequestIdRef = useRef(0);
    const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);
    const [downloadErrorMessage, setDownloadErrorMessage] = useState<string | null>(null);
    const [installerQa, setInstallerQa] = useState<WindowsArtifactQa | null>(null);
    const [installerQaError, setInstallerQaError] = useState<string | null>(null);
    const [isInstallerQaLoading, setIsInstallerQaLoading] = useState(false);
    const [isInstallerQaOpen, setIsInstallerQaOpen] = useState(false);

    useEffect(() => {
        const detectedDefaults = detectClientDownloadDefaults();
        setClientDefaults(detectedDefaults);
        setSelectedOs(detectedDefaults.os);
        setSelectedWindowsArch(detectedDefaults.windowsArch);
        setSelectedWindowsPackageType('installer');
    }, []);

    const ensureReleaseLoaded = async (
        query: SambaLatestReleaseQuery
    ): Promise<SambaLatestReleaseResponse> => {
        const cacheKey = buildReleaseCacheKey(query);
        const cachedRelease = releaseCache[cacheKey];
        if (cachedRelease) {
            return cachedRelease;
        }

        const cachedRequest = releaseRequestCacheRef.current[cacheKey];
        if (cachedRequest !== undefined) {
            return await cachedRequest;
        }

        const releaseRequest = sambaApi
            .getLatestRelease(query)
            .then((response) => SambaLatestReleaseResponseSchema.parse(response))
            .then((validatedResponse) => {
                setReleaseCache((currentCache) => ({
                    ...currentCache,
                    [cacheKey]: currentCache[cacheKey] ?? validatedResponse,
                }));
                return validatedResponse;
            })
            .finally(() => {
                releaseRequestCacheRef.current[cacheKey] = undefined;
            });
        releaseRequestCacheRef.current[cacheKey] = releaseRequest;
        return await releaseRequest;
    };

    useEffect(() => {
        void ensureReleaseLoaded(
            getReleaseQuery(clientDefaults.os, clientDefaults.windowsArch)
        ).catch((error) => {
            setDownloadErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Failed to load Anchorbase download information.'
            );
        });
    }, [clientDefaults]);

    useEffect(() => {
        void ensureReleaseLoaded(getReleaseQuery(selectedOs, selectedWindowsArch)).catch(
            (error) => {
                setDownloadErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Failed to load Anchorbase download information.'
                );
            }
        );
    }, [selectedOs, selectedWindowsArch]);

    const openDownloadUrl = (downloadUrl: string): void => {
        if (document.body === null) {
            throw new Error('Unable to start installer download. Please try again.');
        }
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        downloadLink.href = downloadUrl;
        // Samba saves links with this target as downloads; browsers open them as a new tab.
        downloadLink.target = InstallerDownloadLinkTarget;
        downloadLink.rel = 'noopener noreferrer';
        downloadLink.download = '';
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        try {
            downloadLink.click();
        } finally {
            downloadLink.remove();
        }
    };

    const startDownload = async (resolvePackage: () => Promise<string>): Promise<void> => {
        setDownloadErrorMessage(null);
        setIsDownloadInProgress(true);
        try {
            openDownloadUrl(await resolvePackage());
        } catch (error) {
            setDownloadErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Failed to start Anchorbase installer download.'
            );
        } finally {
            setIsDownloadInProgress(false);
        }
    };

    const openInstallerQa = async (arch: SambaArch): Promise<void> => {
        const requestId = ++installerQaRequestIdRef.current;
        setInstallerQa(null);
        setInstallerQaError(null);
        setIsInstallerQaLoading(true);
        setIsInstallerQaOpen(true);
        try {
            const qa = await sambaApi.getInstallerQa({ track: 'stable', arch });
            if (installerQaRequestIdRef.current === requestId) {
                setInstallerQa(qa);
            }
        } catch (error) {
            if (installerQaRequestIdRef.current === requestId) {
                setInstallerQaError(
                    error instanceof Error ? error.message : 'Failed to load installer metadata.'
                );
            }
        } finally {
            if (installerQaRequestIdRef.current === requestId) {
                setIsInstallerQaLoading(false);
            }
        }
    };

    const primaryReleaseResponse =
        releaseCache[
            buildReleaseCacheKey(getReleaseQuery(clientDefaults.os, clientDefaults.windowsArch))
        ];
    const selectedReleaseResponse =
        releaseCache[buildReleaseCacheKey(getReleaseQuery(selectedOs, selectedWindowsArch))];

    const detectedWindowsInstaller = selectWindowsPackage(
        primaryReleaseResponse,
        clientDefaults.windowsArch,
        'installer'
    );
    const detectedWindowsPortable = selectWindowsPackage(
        primaryReleaseResponse,
        clientDefaults.windowsArch,
        'portable'
    );
    const selectedWindowsPackage = selectWindowsPackage(
        selectedReleaseResponse,
        selectedWindowsArch,
        selectedWindowsPackageType
    );

    const onSelectedOsChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        const nextOs = osSelectionSchema.parse(event.target.value);
        setSelectedOs(nextOs);
        if (nextOs === 'mac') {
            setSelectedWindowsPackageType('installer');
        }
    };

    const onSelectedWindowsArchChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        setSelectedWindowsArch(windowsArchSelectionSchema.parse(event.target.value));
    };

    const onSelectedWindowsPackageTypeChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        setSelectedWindowsPackageType(windowsPackageTypeSelectionSchema.parse(event.target.value));
    };

    const selectedDownloadButtonEnabled =
        selectedOs === 'mac' ? true : selectedWindowsPackage !== null;
    const selectedDownloadButtonLabel: string =
        selectedOs === 'win'
            ? selectedWindowsPackageType === 'installer'
                ? '4. Download installer'
                : '4. Download portable app'
            : '3. Download .dmg';

    return (
        <Dialog open={isInstallerQaOpen} onOpenChange={setIsInstallerQaOpen}>
            <div className="space-y-6">
                <h1 className="text-3xl font-bold">Downloads</h1>

                <Card className="max-w-3xl">
                    <CardHeader>
                        <CardTitle>Recommended for This Device</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            {clientDefaults.os === 'win'
                                ? `Detected Windows ${clientDefaults.windowsArch}. Stable builds are recommended for everyday use.`
                                : `Detected macOS ${clientDefaults.windowsArch}. Stable builds are recommended for everyday use.`}
                        </p>

                        {clientDefaults.os === 'win' ? (
                            <>
                                <Button
                                    type="button"
                                    onClick={() =>
                                        void startDownload(async () => {
                                            const releaseResponse = await ensureReleaseLoaded(
                                                getReleaseQuery('win', clientDefaults.windowsArch)
                                            );
                                            const installerPackage = selectWindowsPackage(
                                                releaseResponse,
                                                clientDefaults.windowsArch,
                                                'installer'
                                            );
                                            if (!installerPackage) {
                                                throw new Error(
                                                    `No stable Windows installer is available for ${clientDefaults.windowsArch}.`
                                                );
                                            }
                                            return installerPackage.downloadUrl;
                                        })
                                    }
                                    disabled={
                                        isDownloadInProgress || detectedWindowsInstaller === null
                                    }
                                    className="w-fit"
                                >
                                    {isDownloadInProgress
                                        ? 'Starting download...'
                                        : 'Anchorbase Installer'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={(event) => {
                                        installerQaTriggerRef.current = event.currentTarget;
                                        openInstallerQa(clientDefaults.windowsArch).catch(() => {
                                            setInstallerQaError(
                                                'Failed to load installer metadata.'
                                            );
                                        });
                                    }}
                                    disabled={detectedWindowsInstaller === null}
                                    className="w-fit"
                                >
                                    View installer details
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        void startDownload(async () => {
                                            const releaseResponse = await ensureReleaseLoaded(
                                                getReleaseQuery('win', clientDefaults.windowsArch)
                                            );
                                            const portablePackage = selectWindowsPackage(
                                                releaseResponse,
                                                clientDefaults.windowsArch,
                                                'portable'
                                            );
                                            if (!portablePackage) {
                                                throw new Error(
                                                    `No stable Windows standalone app is available for ${clientDefaults.windowsArch}.`
                                                );
                                            }
                                            return portablePackage.downloadUrl;
                                        })
                                    }
                                    disabled={
                                        isDownloadInProgress || detectedWindowsPortable === null
                                    }
                                    className="w-fit"
                                >
                                    Anchorbase standalone app
                                </Button>
                            </>
                        ) : (
                            <Button
                                type="button"
                                onClick={() =>
                                    void startDownload(async () => {
                                        const releaseResponse = await ensureReleaseLoaded(
                                            getReleaseQuery('mac', clientDefaults.windowsArch)
                                        );
                                        return releaseResponse.downloadUrl;
                                    })
                                }
                                disabled={
                                    isDownloadInProgress || primaryReleaseResponse === undefined
                                }
                                className="w-fit"
                            >
                                {isDownloadInProgress ? 'Starting download...' : 'Download .dmg'}
                            </Button>
                        )}
                    </CardContent>
                </Card>

                <Card className="max-w-3xl">
                    <CardHeader>
                        <CardTitle>Download a Different Version</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <label
                                htmlFor="download-os"
                                className="text-sm font-medium text-foreground"
                            >
                                1. Operating system
                            </label>
                            <select
                                id="download-os"
                                value={selectedOs}
                                onChange={onSelectedOsChanged}
                                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                                disabled={isDownloadInProgress}
                            >
                                <option value="win">Windows</option>
                                <option value="mac">Mac</option>
                            </select>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label
                                htmlFor="download-arch"
                                className="text-sm font-medium text-foreground"
                            >
                                2. {selectedOs === 'win' ? 'Windows' : 'Mac'} architecture
                            </label>
                            <select
                                id="download-arch"
                                value={selectedWindowsArch}
                                onChange={onSelectedWindowsArchChanged}
                                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                                disabled={isDownloadInProgress}
                            >
                                <option value="x64">
                                    {selectedOs === 'win' ? 'Windows x64' : 'Mac Intel (x64)'}
                                </option>
                                <option value="arm64">
                                    {selectedOs === 'win'
                                        ? 'Windows arm64'
                                        : 'Mac Apple Silicon (arm64)'}
                                </option>
                            </select>
                        </div>

                        {selectedOs === 'win' ? (
                            <>
                                <div className="flex flex-col gap-2">
                                    <label
                                        htmlFor="download-package-type"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        3. Windows package
                                    </label>
                                    <select
                                        id="download-package-type"
                                        value={selectedWindowsPackageType}
                                        onChange={onSelectedWindowsPackageTypeChanged}
                                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                                        disabled={isDownloadInProgress}
                                    >
                                        <option value="installer">Installer</option>
                                        <option value="portable">Portable app</option>
                                    </select>
                                </div>
                            </>
                        ) : null}

                        <Button
                            type="button"
                            onClick={() =>
                                void startDownload(async () => {
                                    const releaseResponse = await ensureReleaseLoaded(
                                        getReleaseQuery(selectedOs, selectedWindowsArch)
                                    );
                                    if (selectedOs === 'mac') {
                                        return releaseResponse.downloadUrl;
                                    }
                                    const windowsPackage = selectWindowsPackage(
                                        releaseResponse,
                                        selectedWindowsArch,
                                        selectedWindowsPackageType
                                    );
                                    if (!windowsPackage) {
                                        throw new Error(
                                            `No stable Windows ${selectedWindowsPackageType} package is available for ${selectedWindowsArch}.`
                                        );
                                    }
                                    return windowsPackage.downloadUrl;
                                })
                            }
                            disabled={isDownloadInProgress || !selectedDownloadButtonEnabled}
                            className="w-fit"
                        >
                            {isDownloadInProgress
                                ? 'Starting download...'
                                : selectedDownloadButtonLabel}
                        </Button>

                        {selectedOs === 'win' && selectedWindowsPackageType === 'installer' ? (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={(event) => {
                                    installerQaTriggerRef.current = event.currentTarget;
                                    openInstallerQa(selectedWindowsArch).catch(() => {
                                        setInstallerQaError('Failed to load installer metadata.');
                                    });
                                }}
                                disabled={selectedWindowsPackage === null}
                                className="w-fit"
                            >
                                View installer details
                            </Button>
                        ) : null}

                        {downloadErrorMessage !== null ? (
                            <p className="text-sm text-destructive">{downloadErrorMessage}</p>
                        ) : null}
                    </CardContent>
                </Card>
            </div>
            <DialogContent
                className="max-h-[85vh] max-w-4xl overflow-hidden"
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    installerQaTriggerRef.current?.focus();
                }}
            >
                <DialogHeader>
                    <DialogTitle>Anchorbase Installer Details</DialogTitle>
                    <DialogDescription>
                        Signature verification and build metadata for the selected installer.
                    </DialogDescription>
                </DialogHeader>
                <div className="overflow-auto">
                    {isInstallerQaLoading ? (
                        <p role="status" className="text-sm text-muted-foreground">
                            Loading installer details…
                        </p>
                    ) : installerQaError !== null ? (
                        <p role="alert" className="text-sm text-destructive">
                            {installerQaError}
                        </p>
                    ) : installerQa !== null ? (
                        <table className="w-full border-collapse text-left text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th scope="col" className="p-2 font-medium">
                                        Property
                                    </th>
                                    <th scope="col" className="p-2 font-medium">
                                        Value
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {flattenInstallerQa(installerQa).map((row) => (
                                    <tr key={row.property} className="border-b align-top">
                                        <th
                                            scope="row"
                                            className="p-2 font-mono text-xs font-medium"
                                        >
                                            {row.property}
                                        </th>
                                        <td className="break-all p-2">{row.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button type="button" onClick={() => setIsInstallerQaOpen(false)}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function DownloadsPage() {
    return (
        <ProtectedRoute>
            <DownloadsRouteContent />
        </ProtectedRoute>
    );
}

function DownloadsRouteContent() {
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await sessionSensitiveAuthApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });

    if (activeRoleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    if (
        !hasDownloadsCapability(
            getActiveRoleAssignmentOperationalResponse(activeRoleAssignmentQuery.data)
        )
    ) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <MainLayout>
            <DownloadsContent />
        </MainLayout>
    );
}
